import React, { Dispatch, FC, SetStateAction, useState } from 'react';
import classNames from 'classnames';
import { DrawerFooter } from '@fiverr-private/overlay';
import { atoms } from '@fiverr-private/theme';
import { Container, Stack } from '@fiverr-private/layout_components';
import { Button, IconButton } from '@fiverr-private/button';
import { BinIcon } from '@fiverr-private/icons';
import { I18n, translate as t } from '@fiverr-private/i18n-react';
import { WorkExperience } from '../../../../gql-operations/gql-generated';
import { CurrentViewState, WorkExperienceViewStates } from '../../shared/types';
import { T_KEY } from '../../utils';
import { deleteButton, deleteIcon } from '../../shared/styles.ve.css';
import DeleteExperienceModal from './DeleteExperienceModal';

interface FormButtonsProps {
    currentExperience?: WorkExperience;
    handleSaveClick: () => void;
    handleDeleteClick: () => void;
    initialIsDirty?: boolean;
    setCurrentViewState: Dispatch<SetStateAction<CurrentViewState>>;
    currentWorkExperiences: WorkExperience[] | null | undefined;
    setCurrentWorkExperiences: (workExperiences: WorkExperience[] | undefined | null) => void;
}

interface DeleteButtonProps {
    isMobile?: boolean;
    setIsDeleteModalOpen: (isOpen: boolean) => void;
}

const DeleteButton: FC<DeleteButtonProps> = ({ isMobile, setIsDeleteModalOpen }) => {
    const ButtonComponent = isMobile ? Button : IconButton;

    return (
        <ButtonComponent
            aria-label={t(`${T_KEY}.delete`)}
            onClick={() => setIsDeleteModalOpen(true)}
            type="reset"
            intent="secondary"
            variant={isMobile ? 'filled' : 'ghost'}
            className={classNames(
                atoms({
                    marginRight: 'auto',
                }),
                isMobile ? deleteIcon : deleteButton
            )}
        >
            {isMobile ? <BinIcon /> : <I18n k={`${T_KEY}.delete`} />}
        </ButtonComponent>
    );
};

const FormButtons: FC<FormButtonsProps> = ({
    currentExperience,
    setCurrentViewState,
    initialIsDirty,
    handleSaveClick,
    handleDeleteClick,
    setCurrentWorkExperiences,
    currentWorkExperiences,
}) => {
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

    return (
        <>
            <DrawerFooter>
                <Stack justifyContent="flexEnd">
                    {currentExperience && (
                        <>
                            <Container
                                hidden={{
                                    default: false,
                                    sm: true,
                                }}
                                marginRight="auto"
                                display="flex"
                                alignItems="center"
                            >
                                <DeleteButton isMobile setIsDeleteModalOpen={setIsDeleteModalOpen} />
                            </Container>
                            <Container
                                hidden={{
                                    default: true,
                                    sm: false,
                                }}
                                marginRight="auto"
                                display="flex"
                                alignItems="center"
                            >
                                <DeleteButton setIsDeleteModalOpen={setIsDeleteModalOpen} />
                            </Container>
                        </>
                    )}
                    <Button
                        intent="secondary"
                        type="reset"
                        onClick={() => {
                            setCurrentViewState((state) => ({
                                ...state,
                                viewState:
                                    currentWorkExperiences && currentWorkExperiences.length > 0
                                        ? WorkExperienceViewStates.List
                                        : WorkExperienceViewStates.Empty,
                                isDirty: initialIsDirty || false,
                            }));
                        }}
                    >
                        <I18n k={`${T_KEY}.cancel`} />
                    </Button>
                    <Button type="submit" onClick={() => handleSaveClick()}>
                        <I18n k={`${T_KEY}.save`} />
                    </Button>
                </Stack>
            </DrawerFooter>
            <DeleteExperienceModal
                isDeleteModalOpen={isDeleteModalOpen}
                setIsDeleteModalOpen={setIsDeleteModalOpen}
                setCurrentViewState={setCurrentViewState}
                setCurrentWorkExperiences={setCurrentWorkExperiences}
                handleDeleteClick={handleDeleteClick}
                currentExperience={currentExperience}
                currentWorkExperiences={currentWorkExperiences}
            />
        </>
    );
};

export default FormButtons;
